import { faPlus, faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { SortTypeEnum } from "@repo/helpers";
import { ComplaintCustomerAction, IncidentStatus } from "@repo/models";
import {
	Button,
	ButtonColor,
	ButtonSize,
	Col,
	Column,
	GridPreLoaded,
	OriginalTimezoneDateColumn,
	Row,
	useAlertContext,
} from "@repo/ui";
import { useCallback, useState } from "react";

import type { Props } from "./types";

import { useComplaintApi, useComplaintQueries } from "../../../../api/complaintApi";
import {
	ComplaintCustomerActionProvider,
	useComplaintContext,
	useComplaintCustomerActionContext,
} from "../../../../contexts/editableItemContext";
import { DeleteAlertDialog } from "../../../deleteAlertDialog";
import CustomerActionsEditor from "./editor";
import style from "./style.module.scss";

export default function ComplaintCustomerActions(props: Props) {
	return (
		<ComplaintCustomerActionProvider>
			<ComplaintCustomerActionsInternal {...props} />
		</ComplaintCustomerActionProvider>
	);
}

export function ComplaintCustomerActionsInternal(props: Props) {
	const { item: complaint } = useComplaintContext();
	const { clearAlert, unsavedChangesAlert } = useAlertContext();
	const { item: customerAction, pendingChanges, overwriteItem } = useComplaintCustomerActionContext();
	const { deleteCustomerAction } = useComplaintApi();

	const { useCustomerActionQuery } = useComplaintQueries();
	const { data, loaded, clearQuery } = useCustomerActionQuery(complaint.id);

	const [columns] = useState(createColumns);
	const [deleteActionId, setDeleteActionId] = useState<number | null>(null);

	const { readOnly } = props;

	function createColumns() {
		return [
			new Column("", "id").withFormat(renderDeleteButton),
			new Column("Action Taken", "actionTakenCategory"),
			new Column("Sub Category", "actionTakenSubCategory"),
			new OriginalTimezoneDateColumn("Created On", "createdOn").dateOnly().withSortBy(SortTypeEnum.descending),
			new Column("Status", "statusId").withFormat((i) => IncidentStatus[i]),
			new Column("Number of Events", "count"),
			new Column("Notes", "notes"),
		] as Column<ComplaintCustomerAction>[];
	}

	function deleteButtonClick(customerActionId: number) {
		setDeleteActionId(customerActionId);
	}

	async function confirmDelete() {
		if (deleteActionId) {
			await deleteCustomerAction(deleteActionId);
			await clearQuery();
			setDeleteActionId(null);
		}
	}

	function renderDeleteButton(customerActionId: number) {
		return (
			<div className={style.editButtons}>
				<Button
					color={ButtonColor.Green}
					onClick={() => deleteButtonClick(customerActionId)}
					size={ButtonSize.medium}
				>
					<FontAwesomeIcon icon={faTimes} size="lg" />
					Delete
				</Button>
			</div>
		);
	}

	const openNew = useCallback(() => {
		clearAlert();
		overwriteItem({
			incidentId: complaint.id,
			count: 1,
			statusId: IncidentStatus.Open,
		} as ComplaintCustomerAction);
	}, [clearAlert, overwriteItem]);

	const newClicked = useCallback(() => {
		if (pendingChanges) unsavedChangesAlert(openNew);
		else openNew();
	}, [openNew, unsavedChangesAlert, pendingChanges]);

	const openItem = useCallback(
		(id: number) => {
			const o = data.get(id);
			overwriteItem(o);
			clearAlert();
		},
		[clearAlert, data, overwriteItem],
	);

	const customerActionId = customerAction?.id;
	const rowClicked = useCallback(
		(id: number) => {
			if (id === customerActionId) return;
			const onConfirm = () => {
				openItem(id);
			};
			if (pendingChanges) unsavedChangesAlert(onConfirm);
			else {
				onConfirm();
			}
		},
		[openItem, unsavedChangesAlert, pendingChanges, customerActionId],
	);

	return (
		<Row>
			<Col sm={1}>
				<Button
					className={style.newButton}
					color={ButtonColor.Green}
					onClick={newClicked}
					size={ButtonSize.medium}
					disabled={readOnly}
				>
					<FontAwesomeIcon icon={faPlus} size="1x" />
					New
				</Button>
			</Col>
			<Col sm={readOnly ? 12 : 11}>
				<GridPreLoaded
					{...props}
					columns={columns}
					values={data?.data ?? []}
					loading={!loaded}
					getData={clearQuery}
					onClickRow={rowClicked}
				/>
				<hr />
				<CustomerActionsEditor />
			</Col>
			<DeleteAlertDialog
				isOpen={!!deleteActionId}
				onClose={() => setDeleteActionId(null)}
				onConfirm={confirmDelete}
				title="Delete Customer Action"
				description="Are you sure you want to delete this customer action?"
			/>
		</Row>
	);
}

ComplaintCustomerActions.defaultProps = {
	title: null,
	id: "cmplCustActGrid",
	values: [],
	keyProperty: "id",
	pageSize: 5,
} as Props;
